import { IManufacturerResponse } from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceRequest,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export const ManufacturersDataSourceKey = 'manufacturers';

export type ManufacturersFilter = Readonly<{
    name?: string;
    corporationId?: string;
    isApproved?: boolean;
    nameSearch?: string;
    exactCaseInsensitiveStrings?: string[];
}>;

export type UseManufacturersDataSourceOptions = BaseDataSourceOptions<
    IManufacturerResponse,
    ManufacturersFilter
>;

export type ManufacturersPagedDataSource = PagedDataSource<
    IManufacturerResponse,
    ManufacturersFilter
>;

export type ManufacturersInfiniteDataSource = InfiniteDataSource<
    IManufacturerResponse,
    ManufacturersFilter
>;

export const getManufacturersFilterParams = (
    {
        filter: {
            name,
            corporationId,
            isApproved,
            exactCaseInsensitiveStrings,
            nameSearch,
        },
        sort,
        page,
        pageLimit,
    }: DataSourceRequest<ManufacturersFilter>,
    isDetail: boolean
) => {
    const CORPORATION_FIELD_NAME = isDetail ? 'Corporation' : 'CorporationId';
    const filterExpr = Filter.and(
        name !== undefined ? Filter.eq('Name', name) : undefined,
        Filter.or(
            corporationId ? Filter.eq('CorporationId', corporationId) : undefined,
            isApproved ? Filter.isNull(CORPORATION_FIELD_NAME) : undefined
        ),
        isApproved === false ? Filter.isNotNull(CORPORATION_FIELD_NAME) : undefined,
        nameSearch
            ? Filter.or(
                  Filter.startsWith('Name', nameSearch),
                  isDetail
                      ? Filter.contains('Corporation.CorporationName', nameSearch)
                      : undefined
              )
            : undefined,
        exactCaseInsensitiveStrings
            ? Filter.or(
                  ...exactCaseInsensitiveStrings.map((s) =>
                      Filter.caseInsensitiveEq('Name', s)
                  )
              )
            : undefined
    );
    const orderBy = buildOrderBy(sort);

    return { filterExpr, orderBy, page, pageLimit };
};

const getAllManufacturers: DataSourceQuery<IManufacturerResponse, ManufacturersFilter> = (
    params
) => {
    const { filterExpr, orderBy, page, pageLimit } = getManufacturersFilterParams(
        params,
        false
    );
    return toDataSourceResult(
        sdClient.getManufacturers({
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
        })
    );
};

export function usePagedManufacturersDataSource({
    ...options
}: UseManufacturersDataSourceOptions): ManufacturersPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllManufacturers,
    });
}

export function useInfiniteManufacturersDataSource({
    ...options
}: UseManufacturersDataSourceOptions): ManufacturersInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllManufacturers,
    });
}

export function invalidateManufacturersDataSource() {
    queryClient.invalidateQueries({
        queryKey: [ManufacturersDataSourceKey],
    });
}
